/**
 * Capture canvas element as image blob using html2canvas
 * html2canvas correctly captures embedded SVG elements, including vertical text with writing-mode
 * @param {HTMLElement} element - The DOM element to capture
 * @param {Object} options - html2canvas options
 * @returns {Promise<Blob>}
 */
export const captureCanvas = async (element, options = {}) => {
  if (!element) {
    throw new Error("Element is null or undefined");
  }
  
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    throw new Error(`Element has invalid dimensions: ${rect.width}x${rect.height}`);
  }

  const html2canvas = (await import("html2canvas")).default;
  const initialScale = options.scale !== undefined ? options.scale : 2;
  const scalesToTry = [initialScale, 1.5, 1.0];
  let lastError = null;

  for (const scale of scalesToTry) {
    try {
      // Calculate full element dimensions including scrollable content
      // Use scrollWidth/scrollHeight to capture full content, not just viewport
      const scrollWidth = element.scrollWidth || rect.width;
      const scrollHeight = element.scrollHeight || rect.height;
      
      const defaultOptions = {
        scale,
        useCORS: true,
        allowTaint: false,
        logging: false,
        backgroundColor: null, // null = transparent background (PNG supports alpha channel)
        // Force html2canvas to capture full element including overflow content
        width: options.width || scrollWidth,
        height: options.height || scrollHeight,
        ...options,
      };

      let canvas = await html2canvas(element, defaultOptions);
      
      if (!canvas) {
        throw new Error("html2canvas returned null canvas");
      }
      
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error(`Canvas has invalid dimensions: ${canvas.width}x${canvas.height}`);
      }
      
      // Check if canvas is too large (browser limit is typically 32767x32767)
      const maxDimension = 32767;
      if (canvas.width > maxDimension || canvas.height > maxDimension) {
        if (scale > scalesToTry[scalesToTry.length - 1]) {
          lastError = new Error(`Canvas too large: ${canvas.width}x${canvas.height} (max: ${maxDimension})`);
          continue; // Try with lower scale
        }
        throw new Error(`Canvas too large: ${canvas.width}x${canvas.height} (max: ${maxDimension})`);
      }

      // Convert canvas to blob with timeout
      const blob = await new Promise((resolve, reject) => {
        let timeoutId = setTimeout(() => {
          reject(new Error("toBlob timeout - canvas conversion took too long"));
        }, 30000); // 30 second timeout
        
        canvas.toBlob(
          (blob) => {
            clearTimeout(timeoutId);
            if (!blob) {
              const errorMsg = `Failed to create image blob. Canvas size: ${canvas.width}x${canvas.height}, Element size: ${rect.width}x${rect.height}`;
              reject(new Error(errorMsg));
            } else {
              resolve(blob);
            }
          },
          "image/png",
          1.0
        );
      });
      
      if (scale < initialScale) {
        console.warn(`Canvas captured with reduced scale (${scale} instead of ${initialScale}) to avoid memory issues`);
      }
      
      return blob;
    } catch (error) {
      lastError = error;
      if (scale === scalesToTry[scalesToTry.length - 1]) {
        // Last scale attempt, throw the error
        throw error;
      }
      console.warn(`Capture failed with scale ${scale}, trying with lower scale...`, error.message);
      continue;
    }
  }
  
  throw lastError || new Error("Failed to capture canvas after all retry attempts");
};

/**
 * Wait for images to load in the element
 * @param {HTMLElement} element
 * @returns {Promise<void>}
 */
export const waitForImages = async (element) => {
  const images = element.querySelectorAll("img");
  const imageLoadPromises = Array.from(images).map((img) => {
    if (img.complete) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      // Timeout after 5 seconds
      setTimeout(() => reject(new Error("Image load timeout")), 5000);
    });
  });

  await Promise.all(imageLoadPromises);
};

/**
 * Preload all background images from CSS to ensure they're loaded with CORS before html2canvas capture
 * This is critical for S3 images that need CORS headers
 * @param {HTMLElement} element - The DOM element to scan for background images
 * @returns {Promise<void>}
 */
export const preloadBackgroundImages = async (element) => {
  const imageUrls = new Set();

  // Find all elements with background images
  const allElements = element.querySelectorAll("*");

  // Extract background image URLs from computed styles
  allElements.forEach((el) => {
    const style = window.getComputedStyle(el);
    const bgImage = style.backgroundImage;

    // Extract URL from backgroundImage (format: url("...") or url(...))
    if (bgImage && bgImage !== "none" && bgImage !== "") {
      const urlMatch = bgImage.match(/url\(["']?([^"']+)["']?\)/);
      if (urlMatch && urlMatch[1]) {
        const imageUrl = urlMatch[1];
        // Only add if it's an external URL (not data: or blob:)
        if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
          imageUrls.add(imageUrl);
        }
      }
    }
  });

  // Preload all images with CORS
  const preloadPromises = Array.from(imageUrls).map((url) => {
    return new Promise((resolve) => {
      const img = new Image();
      let resolved = false;
      let timeoutId = null;

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      // CRITICAL: Set crossOrigin to enable CORS
      // This allows html2canvas to access the image data
      img.crossOrigin = "anonymous";

      img.onload = () => {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve();
        }
      };

      img.onerror = (event) => {
        // Log but don't fail - some images might fail to load but that's okay
        // This can happen if CORS is not properly configured on S3
        if (!resolved) {
          resolved = true;
          cleanup();
          console.warn(`Failed to preload background image: ${url}`, event);
          resolve(); // Resolve anyway to not block capture
        }
      };

      // Timeout after 5 seconds
      timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          cleanup();
          console.warn(`Timeout preloading background image: ${url}`);
          resolve(); // Resolve anyway to not block capture
        }
      }, 5000);

      img.src = url;
    });
  });

  await Promise.all(preloadPromises);
};

